export type BrandDetail = {
  slug: string;
  name: string;
  displayName?: string;
  logoName: string;
  heroImage: string;
  heroFit?: "cover" | "contain";
  officialUrl?: string;
  officialLabel?: string;
  summary: string;
  overview: string;
  highlights: string[];
  category: string;
  markets: string[];
  relationship: string;
  sourceNote?: string;
  galleryImages?: Array<{
    src: string;
    label: string;
    fit?: "cover" | "contain";
  }>;
};

export const brandDirectory: BrandDetail[] = [
  {
    slug: "melomeli",
    name: "melomeli",
    logoName: "melomeli",
    heroImage: "/melomeli-water-01.jpg",
    officialUrl: "https://www.melomeli.co.kr/",
    officialLabel: "Owned Brand · Official Site",
    summary: "QIANDU's owned beauty brand, balancing youthful makeup heritage with a softer skincare presentation.",
    overview:
      "melomeli is QIANDU's in-house beauty brand platform. Public brand materials show a broader beauty range spanning color makeup and cleansing care, while the current site presentation focuses on a calmer skincare-led visual direction.",
    highlights: ["Owned beauty brand", "Makeup and cleansing range", "Flexible for regional launches"],
    category: "Brand Owner",
    markets: ["Global", "APAC"],
    relationship: "QIANDU is the brand owner.",
    sourceNote: "Official melomeli site and internal brand assets",
    galleryImages: [
      { src: "/official-brands/melomeli-logo.png", label: "Official logo", fit: "contain" },
      { src: "/melomeli-hero.jpg", label: "Hydration visual" },
      { src: "/melomeli-water-01.jpg", label: "Cleansing water campaign" },
      { src: "/melomeli-water-02.jpg", label: "Product close-up" },
    ],
  },
  {
    slug: "round-lab",
    name: "ROUND LAB",
    logoName: "ROUND LAB",
    heroImage: "/official-brands/roundlab.jpg",
    officialUrl: "https://roundlab.com/en-ca",
    officialLabel: "Official Site",
    summary: "A Korean skincare brand recognized for its minimalist formulas and the clear retail identity of the Dokdo line.",
    overview:
      "ROUND LAB pairs calm ingredient-led skincare with one of the clearest hero franchises in K-Beauty, making it a strong fit for premium distribution and everyday skincare storytelling.",
    highlights: ["Dokdo hero franchise", "Minimal daily-care positioning", "Premium distribution fit"],
    category: "China General Distribution",
    markets: ["China", "Korea"],
    relationship: "Exclusive China general distributor for Dokdo line products.",
    sourceNote: "Summary based on official site",
    galleryImages: [
      { src: "/official-brands/roundlab.jpg", label: "Campaign visual" },
      { src: "/official-brands/roundlab-banner.png", label: "Brand banner", fit: "contain" },
      { src: "/official-brands/roundlab-logo.png", label: "Official logo", fit: "contain" },
    ],
  },
  {
    slug: "ilso",
    name: "ILSO",
    logoName: "ILSO",
    heroImage: "/official-brands/ilso-share-optimized.jpg",
    officialUrl: "https://ilso.kr",
    officialLabel: "Official Site",
    summary: "A clean Korean skincare brand with a refined visual identity and strong distributor fit across Southeast Asia.",
    overview:
      "ILSO brings a focused skincare proposition, calm visual language, and clear sell-in potential for localized distributor and retail conversations.",
    highlights: ["Clean skincare identity", "Southeast Asia market fit", "Distributor-ready presentation"],
    category: "Vietnam & Myanmar Distribution",
    markets: ["Vietnam", "Myanmar"],
    relationship: "Exclusive general distributor for Vietnam and Myanmar.",
    sourceNote: "Official site and internal market positioning",
    galleryImages: [
      { src: "/official-brands/ilso-share-optimized.jpg", label: "Campaign visual" },
      { src: "/yomei-materials/ilso-book.png", label: "Brand material", fit: "contain" },
      { src: "/official-brands/ilso-logo.svg", label: "Official logo", fit: "contain" },
    ],
  },
  {
    slug: "jmsolution",
    name: "JMSOLUTION",
    logoName: "JMSOLUTION",
    heroImage: "/official-brands/jmsolution-logo.png",
    heroFit: "contain",
    officialUrl: "https://jmsolution.co",
    officialLabel: "Official Site",
    summary: "A Korean skincare brand known for mask-led routines, toner pads, and accessible mass-premium daily care.",
    overview:
      "JMsolution gives the portfolio a broad skincare format story, combining masks, pads, and daily essentials that are easy to present across contemporary retail channels.",
    highlights: ["Mask-led skincare formats", "Pad and routine story", "Mass-premium retail appeal"],
    category: "Agency Partner",
    markets: ["APAC", "Global"],
    relationship: "Selective agency representation.",
    sourceNote: "Summary based on official public store content",
  },
  {
    slug: "jung-saem-mool",
    name: "JUNG SAEM MOOL",
    logoName: "JUNG SAEM MOOL",
    heroImage: "/official-brands/jsm-logo.png",
    heroFit: "contain",
    officialUrl: "https://jsmbeauty.com",
    officialLabel: "Official Site",
    summary: "A premium Korean makeup brand built around the artistry and philosophy of Jung Saem Mool.",
    overview:
      "JUNG SAEM MOOL is built around complexion artistry and refined makeup expression, giving it a strong premium identity for curated market entry.",
    highlights: ["Artist-led philosophy", "Premium complexion positioning", "High-trust presentation"],
    category: "Agency Partner",
    markets: ["APAC", "Global"],
    relationship: "Selective agency representation.",
    sourceNote: "Summary based on official site and brand story",
  },
  {
    slug: "sungboon-editor",
    name: "SUNGBOON EDITOR",
    logoName: "SUNGBOON EDITOR",
    heroImage: "/yomei-materials/sungboon-book.png",
    heroFit: "contain",
    officialUrl: "https://sungbooneditor.com",
    officialLabel: "Official Site",
    summary: "A Korean skincare brand centered on ingredients, collagen, peptides, and targeted glow care.",
    overview:
      "SUNGBOON EDITOR combines ingredient-led formulas with a clear education story, giving distributors an easy way to present collagen, peptide, and treatment-focused care.",
    highlights: ["Ingredient-led skincare", "Collagen and peptide focus", "Strong education story"],
    category: "Agency Partner",
    markets: ["APAC", "Global"],
    relationship: "Selective agency representation.",
    sourceNote: "Summary based on official site",
    galleryImages: [
      { src: "/yomei-materials/sungboon-book.png", label: "Brand material", fit: "contain" },
      { src: "/official-brands/sungboon-logo.png", label: "Official logo", fit: "contain" },
    ],
  },
  {
    slug: "vt",
    name: "VT",
    logoName: "VT",
    heroImage: "/official-brands/vt-logo.svg",
    heroFit: "contain",
    officialUrl: "https://globalvt-cosmetics.com",
    officialLabel: "Official Site",
    summary: "A Korean beauty brand that blends trend-led skincare with high-visibility beauty-tech product stories.",
    overview:
      "VT adds strong commercial energy to the portfolio through hero technologies such as Reedle Shot and a broad range suited to modern beauty retail.",
    highlights: ["Hero technology franchise", "High consumer recognition", "Trend-led retail fit"],
    category: "Agency Partner",
    markets: ["APAC", "Global"],
    relationship: "Selective agency representation.",
    sourceNote: "Summary based on official site and product pages",
  },
  {
    slug: "medicube",
    name: "MEDICUBE",
    logoName: "MEDICUBE",
    heroImage: "/official-brands/medicube-logo.png",
    heroFit: "contain",
    officialUrl: "https://medicube.us",
    officialLabel: "Official Site",
    summary: "A Korean beauty brand known for combining skincare with device-led beauty technology.",
    overview:
      "MEDICUBE strengthens the portfolio's skin-tech presence, with devices and routines that support premium positioning and digital-first product education.",
    highlights: ["Beauty-tech positioning", "Device plus skincare ecosystem", "Premium digital presentation"],
    category: "Agency Partner",
    markets: ["APAC", "Global"],
    relationship: "Selective agency representation.",
    sourceNote: "Summary based on official beauty-tech pages",
  },
  {
    slug: "mediheal",
    name: "MEDIHEAL",
    logoName: "MEDIHEAL",
    heroImage: "/official-brands/mediheal-logo.png",
    heroFit: "contain",
    officialUrl: "https://mediheal.com",
    officialLabel: "Official Site",
    summary: "A global Korean beauty brand best known for sheet masks and routine-based skincare.",
    overview:
      "MEDIHEAL offers one of the clearest mass-premium skincare stories in Korean beauty, with strong recognition in sheet masks and practical daily care.",
    highlights: ["Sheet mask heritage", "Global consumer awareness", "Routine-based communication"],
    category: "Agency Partner",
    markets: ["APAC", "Global"],
    relationship: "Selective agency representation.",
    sourceNote: "Summary based on official site and press materials",
  },
  {
    slug: "ongredients",
    name: "ONGREDIENTS",
    logoName: "ONGREDIENTS",
    heroImage: "/official-brands/ongredients-logo.svg",
    heroFit: "contain",
    officialUrl: "https://en.ongredients.com/layout/basic/about.html",
    officialLabel: "Official Site",
    summary: "A Korean clean beauty brand focused on ingredient clarity, gentle formulas, and sustainable packaging choices.",
    overview:
      "ONGREDIENTS supports a cleaner portfolio narrative with its natural clean beauty message, ingredient transparency, and environmentally conscious packaging direction.",
    highlights: ["Natural clean beauty message", "Mild ingredient story", "Sustainability-led packaging"],
    category: "Agency Partner",
    markets: ["APAC", "Global"],
    relationship: "Selective agency representation.",
    sourceNote: "Summary based on official about page",
  },
  {
    slug: "juice-to-cleanse",
    name: "juice to cleanse",
    logoName: "juice to cleanse",
    heroImage: "/yomei-materials/juicetocleanse-book.png",
    heroFit: "contain",
    officialUrl: "https://juicetocleanse.com",
    officialLabel: "Official Site",
    summary: "A Korean skincare brand with a fresh, modern image centered on cleansing and daily skin comfort.",
    overview:
      "juice to cleanse fits a younger, lifestyle-friendly skincare presentation and adds a softer, routine-led tone to the broader brand portfolio.",
    highlights: ["Fresh brand mood", "Everyday skincare appeal", "Good fit for younger consumers"],
    category: "Agency Partner",
    markets: ["APAC", "Global"],
    relationship: "Selective agency representation.",
    sourceNote: "Summary based on official site",
    galleryImages: [
      { src: "/yomei-materials/juicetocleanse-book.png", label: "Brand material", fit: "contain" },
      { src: "/official-brands/juice-to-cleanse-logo.png", label: "Official logo", fit: "contain" },
    ],
  },
  {
    slug: "mihwanghoo",
    name: "Mihwanghoo",
    displayName: "Mihwanghoo / 米皇后",
    logoName: "Mihwanghoo",
    heroImage: "/mihwanghoo-materials/mihwanghoo-brand-book.png",
    heroFit: "contain",
    officialLabel: "Brand Materials",
    summary: "A beauty line presented through QIANDU's curated product materials, spanning body, hair, and color lines.",
    overview:
      "Mihwanghoo expands the portfolio with a wider care structure that supports distributor presentation across beauty, personal care, and export-oriented product lines.",
    highlights: ["Body care line", "Hair care line", "Color cream and export materials"],
    category: "Portfolio Brand",
    markets: ["APAC", "Export"],
    relationship: "Presented through QIANDU brand materials.",
    sourceNote: "Summary based on supplied brand materials",
    galleryImages: [
      { src: "/mihwanghoo-materials/mihwanghoo-brand-book.png", label: "Brand book", fit: "contain" },
      { src: "/mihwanghoo-materials/mihwanghoo-body-line.png", label: "Body care line", fit: "contain" },
      { src: "/mihwanghoo-materials/mihwanghoo-hair-line.png", label: "Hair care line", fit: "contain" },
      { src: "/mihwanghoo-materials/mihwanghoo-color-cream.png", label: "Color cream", fit: "contain" },
    ],
  },
  {
    slug: "cnp",
    name: "CNP",
    displayName: "CNP Laboratory",
    logoName: "CNP",
    heroImage: "/official-brands/cnp-hero.jpg",
    officialUrl: "https://www.thecnplaboratory.com",
    officialLabel: "Official Site",
    summary: "A clinical Korean skincare brand with clear derma positioning and strong trust value.",
    overview:
      "CNP adds a clinical skincare layer to the QIANDU portfolio, fitting pharmacy-style presentation, expert skincare communication, and higher-trust retail environments.",
    highlights: ["Clinical skincare positioning", "Derma credibility", "Trusted education value"],
    category: "Agency Partner",
    markets: ["APAC", "Global"],
    relationship: "Selective agency representation.",
    sourceNote: "Summary based on official brand direction and supplied portfolio list",
    galleryImages: [
      { src: "/official-brands/cnp-hero.jpg", label: "Brand visual" },
      { src: "/official-brands/cnp-toner.png", label: "Product detail", fit: "contain" },
      { src: "/official-brands/cnp-logo.png", label: "Official logo", fit: "contain" },
    ],
  },
  {
    slug: "snp",
    name: "SNP",
    logoName: "SNP",
    heroImage: "/brand-placeholders/snp.svg",
    heroFit: "contain",
    officialUrl: "https://www.snpbeauty.com/brand/",
    officialLabel: "Official Site",
    summary: "A Korean total skin solution brand built around skin science, routine care, and functional skincare.",
    overview:
      "SNP presents a broad total-solution skincare story grounded in skin expertise, tested formulas, and accessible routine-led communication.",
    highlights: ["Total skin solution story", "Skin science positioning", "Functional mask heritage"],
    category: "Agency Partner",
    markets: ["APAC", "Global"],
    relationship: "Selective agency representation.",
    sourceNote: "Summary based on SNP Beauty official brand pages",
    galleryImages: [
      { src: "/brand-placeholders/snp.svg", label: "Brand card", fit: "contain" },
    ],
  },
  {
    slug: "belif",
    name: "belif",
    logoName: "belif",
    heroImage: "/brand-placeholders/belif.svg",
    heroFit: "contain",
    officialUrl: "https://lgbeauty.com/brands/belif",
    officialLabel: "LG Beauty Official",
    summary: "An LG H&H skincare brand known for hydration care, Aqua Bomb recognition, and strong global retail familiarity.",
    overview:
      "belif strengthens the portfolio with a recognizable hydration franchise built around Aqua Bomb, Moisturizing Bomb, and an easy-to-understand skincare message.",
    highlights: ["LG H&H portfolio brand", "Aqua Bomb recognition", "Global hydration franchise"],
    category: "LG H&H Agency Partner",
    markets: ["APAC", "Global"],
    relationship: "QIANDU represents selected belif opportunities under the LG H&H agency portfolio.",
    sourceNote: "Summary based on LG Beauty official belif brand page and current public product storefront",
    galleryImages: [
      { src: "/brand-placeholders/belif.svg", label: "Brand card", fit: "contain" },
    ],
  },
  {
    slug: "beyond",
    name: "BEYOND",
    logoName: "BEYOND",
    heroImage: "/brand-placeholders/beyond.svg",
    heroFit: "contain",
    officialUrl: "https://www.lghnh.com/brand/detail.jsp?bid1=H078&gbn=1",
    officialLabel: "LG H&H Official",
    summary: "An LG H&H eco-beauty brand spanning skincare, body care, and hair care.",
    overview:
      "BEYOND adds an eco-beauty and daily-care layer to the portfolio, with a wider category range that supports both skincare and personal-care retail discussions.",
    highlights: ["LG H&H portfolio brand", "Eco-beauty positioning", "Daily-care category breadth"],
    category: "LG H&H Agency Partner",
    markets: ["APAC", "Global"],
    relationship: "QIANDU represents selected BEYOND opportunities under the LG H&H agency portfolio.",
    sourceNote: "Summary based on LG H&H official BEYOND brand page",
    galleryImages: [
      { src: "/brand-placeholders/beyond.svg", label: "Brand card", fit: "contain" },
    ],
  },
  {
    slug: "rataplan",
    name: "RATAPLAN",
    logoName: "RATAPLAN",
    heroImage: "/yomei-materials/rataplan-book.png",
    heroFit: "contain",
    officialUrl: "https://rataplan.co.kr",
    officialLabel: "Official Site",
    summary: "A Korean clean-beauty skincare brand rooted in Korean natural ingredients and seasonal care stories.",
    overview:
      "RATAPLAN positions itself as Korean clean beauty, reinterpreting Korean natural ingredients through skin-beneficial technology. Public product stories include Minari soothing care, deep-sea hydration, sun care, and brightening serum lines.",
    highlights: ["Korean clean beauty", "Korean natural ingredient story", "Minari, deep-sea, sun care, and brightening lines"],
    category: "Thailand & Myanmar Distribution",
    markets: ["Thailand", "Myanmar"],
    relationship: "Exclusive general distributor for Thailand and Myanmar.",
    sourceNote: "Summary based on RATAPLAN official Korean and English brand pages plus supplied distribution structure",
    galleryImages: [
      { src: "/yomei-materials/rataplan-book.png", label: "Brand material", fit: "contain" },
    ],
  },
  {
    slug: "de-noah",
    name: "de noah",
    logoName: "de noah",
    heroImage: "/yomei-materials/denoah-book.png",
    heroFit: "contain",
    officialUrl: "https://de-noah.com/",
    officialLabel: "Global General Distribution",
    summary: "A Korean skincare brand centered on rest, time, and choice, with relaxation-led daily care.",
    overview:
      "de noah's official public site presents a cozy, relaxation-led skincare concept with products such as collagen masks and wrinkle care creams.",
    highlights: ["Relaxation-led skincare concept", "Daily care and anti-aging focus", "Global distribution role"],
    category: "Global General Distribution",
    markets: ["Global"],
    relationship: "QIANDU is global general distributor.",
    sourceNote: "Summary based on de noah official public site and supplied brand relationship",
    galleryImages: [
      { src: "/yomei-materials/denoah-book.png", label: "Brand material", fit: "contain" },
    ],
  },
  {
    slug: "calvisana",
    name: "Calvisana",
    logoName: "Calvisana",
    heroImage: "/brand-placeholders/calvisana.svg",
    heroFit: "contain",
    officialLabel: "Global General Distribution",
    summary: "A premium caviar-positioned portfolio line under QIANDU's global distribution structure.",
    overview:
      "Calvisana is kept in the portfolio as a premium caviar-positioned commercial line. Public beauty-brand source material is still limited, so the external presentation remains intentionally restrained.",
    highlights: ["Premium caviar positioning", "Global distribution role", "Public beauty-source verification pending"],
    category: "Global General Distribution",
    markets: ["Global"],
    relationship: "QIANDU is global general distributor.",
    sourceNote: "Public beauty-brand source verification is still pending; current presentation follows supplied brand relationship",
    galleryImages: [
      { src: "/brand-placeholders/calvisana.svg", label: "Brand card", fit: "contain" },
    ],
  },
  {
    slug: "efilow",
    name: "efilow",
    logoName: "efilow",
    heroImage: "/brand-placeholders/efilow.svg",
    heroFit: "contain",
    officialUrl: "https://www.efilow.com/",
    officialLabel: "Official Site",
    summary: "A Korean slow-beauty skincare brand with a calm routine focus and public official-store presence.",
    overview:
      "The official efilow store presents a slow-beauty positioning with toner, ampoule, pad, cream, and mask lines built around ingredient-led daily skin-comfort routines.",
    highlights: ["Official Korean brand store", "Slow-beauty routine positioning", "Pad, toner, ampoule, and cream assortment"],
    category: "Partial Product Agency",
    markets: ["APAC", "Southeast Asia"],
    relationship: "QIANDU represents selected efilow product opportunities.",
    sourceNote: "Summary based on the efilow official public store",
    galleryImages: [
      { src: "/brand-placeholders/efilow.svg", label: "Brand card", fit: "contain" },
    ],
  },
  {
    slug: "wenlici",
    name: "WENLICI",
    displayName: "ONGREDIENTS / 温丽慈",
    logoName: "WENLICI",
    heroImage: "/official-brands/ongredients-logo.svg",
    heroFit: "contain",
    officialUrl: "https://ongredients.com/",
    officialLabel: "Official Site",
    summary: "A Korean clean-beauty brand publicly associated with the Chinese market name 温丽慈.",
    overview:
      "Public reporting identifies Ongredients（温丽慈）as a Korean beauty brand under Power Player Co., Ltd. The brand is described around natural, mild, organic, and sustainable positioning across core skincare categories.",
    highlights: ["Korean clean-beauty positioning", "Public China-market naming confirmed", "38-product public assortment reference"],
    category: "Partial Product Agency",
    markets: ["China", "APAC"],
    relationship: "QIANDU handles selected 温丽慈 product opportunities subject to territory and authorization confirmation.",
    sourceNote:
      "Summary based on the ONGREDIENTS official site plus public reports from Beijing Business Today, Lily&Beauty, Sina, and Ebrun that identify Ongredients as 温丽慈 in China-market communication",
    galleryImages: [
      { src: "/official-brands/ongredients-logo.svg", label: "Official logo", fit: "contain" },
    ],
  },
  {
    slug: "bonabella",
    name: "BONABELLA",
    logoName: "BONABELLA",
    heroImage: "/brand-placeholders/bonabella.svg",
    heroFit: "contain",
    officialUrl: "https://bonabella.co/pages/about-us",
    officialLabel: "Official Site",
    summary: "A beauty brand positioned around simple self-care and accessible skincare essentials.",
    overview:
      "BONABELLA's public about page presents a simple skincare promise across moisturizers, cleansers, sunscreen, and serum, with an emphasis on quality and everyday ease.",
    highlights: ["Simplified skincare", "Moisturizer, cleanser, sunscreen, and serum focus", "Selective market fit"],
    category: "Agency Partner",
    markets: ["APAC", "Global"],
    relationship: "Selective agency representation.",
    sourceNote: "Summary based on Bonabella public about page",
    galleryImages: [
      { src: "/brand-placeholders/bonabella.svg", label: "Brand card", fit: "contain" },
    ],
  },
  {
    slug: "numbuzin",
    name: "NUMBUZIN",
    logoName: "NUMBUZIN",
    heroImage: "/brand-placeholders/numbuzin.svg",
    heroFit: "contain",
    officialUrl: "https://us.numbuzin.com/pages/numbuzin",
    officialLabel: "Official Site",
    summary: "A Korean skincare brand that turns skin concerns into a simple numbered solution system.",
    overview:
      "numbuzin describes a numbered routine system for concerns such as redness, radiance, pore care, dark spots, and fine lines, making product selection easy for consumers.",
    highlights: ["Numbered skin concern system", "Clear consumer education angle", "Strong digital communication format"],
    category: "Agency Partner",
    markets: ["APAC", "Global"],
    relationship: "Selective agency representation.",
    sourceNote: "Summary based on numbuzin official public about page",
    galleryImages: [
      { src: "/brand-placeholders/numbuzin.svg", label: "Brand card", fit: "contain" },
    ],
  },
  {
    slug: "clio",
    name: "CLIO",
    logoName: "CLIO",
    heroImage: "/brand-placeholders/clio.svg",
    heroFit: "contain",
    officialUrl: "https://cliocosmetic.com/en/aboutus/story.asp",
    officialLabel: "Official Site",
    summary: "A Korean professional color makeup brand with long-standing category authority and strong retail familiarity.",
    overview:
      "CLIO brings a professional makeup identity to the portfolio, with strong legacy, clear category recognition, and broad cross-border retail potential.",
    highlights: ["Professional makeup positioning", "Long brand history", "High category recognition"],
    category: "Agency Partner",
    markets: ["APAC", "Global"],
    relationship: "Selective agency representation.",
    sourceNote: "Summary based on official brand story",
    galleryImages: [
      { src: "/brand-placeholders/clio.svg", label: "Brand card", fit: "contain" },
    ],
  },
  {
    slug: "the-palace-secret",
    name: "THE PALACE SECRET",
    logoName: "THE PALACE SECRET",
    heroImage: "/brand-placeholders/the-palace-secret.svg",
    heroFit: "contain",
    summary: "A prestige-positioned beauty portfolio entry kept in a restrained public presentation state.",
    overview:
      "THE PALACE SECRET remains in QIANDU's wider brand references, but stable official public source material has not yet been confirmed with enough confidence for a stronger external product presentation.",
    highlights: ["Prestige portfolio entry", "Public official-source verification pending", "Best kept to restrained public presentation"],
    category: "Portfolio Brand",
    markets: ["APAC", "Global"],
    relationship: "Presented as a QIANDU portfolio brand for selected market opportunities.",
    sourceNote: "Public official-source verification pending",
    galleryImages: [
      { src: "/brand-placeholders/the-palace-secret.svg", label: "Brand card", fit: "contain" },
    ],
  },
  {
    slug: "wakemake",
    name: "WAKEMAKE",
    logoName: "WAKEMAKE",
    heroImage: "/yomei-materials/wakemake-book.png",
    heroFit: "contain",
    officialUrl: "https://clubclio.co.kr",
    officialLabel: "Official Brand Group",
    summary: "A Korean color cosmetics line suited to trend-led makeup presentation and younger retail audiences.",
    overview:
      "WAKEMAKE adds a contemporary color cosmetics angle to the portfolio, fitting modern retail, fast-moving launches, and visual merchandising programs.",
    highlights: ["Trend-led makeup mood", "Young retail appeal", "Good fit for visual launches"],
    category: "Agency Partner",
    markets: ["APAC", "Global"],
    relationship: "Presented under the wider CLIO beauty group ecosystem.",
    sourceNote: "Summary based on official brand group context",
    galleryImages: [
      { src: "/yomei-materials/wakemake-book.png", label: "Brand material", fit: "contain" },
    ],
  },
  {
    slug: "real-derma",
    name: "Real Derma",
    logoName: "Real Derma",
    heroImage: "/brand-placeholders/realderma.svg",
    heroFit: "contain",
    summary: "A derma-care portfolio entry currently kept in a low-claim public presentation state.",
    overview:
      "Real Derma remains in QIANDU's wider portfolio references, but a stable public official Korean brand source has not yet been confirmed with enough confidence for a stronger external presentation.",
    highlights: ["Derma-care portfolio entry", "Public official-source verification pending", "Best kept to restrained public presentation"],
    category: "Portfolio Brand",
    markets: ["APAC", "Global"],
    relationship: "Presented as a QIANDU portfolio brand for selected partner discussions.",
    sourceNote: "Public official-source verification pending",
    galleryImages: [
      { src: "/brand-placeholders/realderma.svg", label: "Brand card", fit: "contain" },
    ],
  },
];

export const brandDirectoryMap = Object.fromEntries(
  brandDirectory.map((brand) => [brand.slug, brand])
) as Record<string, BrandDetail>;

const preferredBrandOrder = [
  "round-lab",
  "cnp",
  "belif",
  "beyond",
  "ilso",
  "jmsolution",
  "snp",
  "jung-saem-mool",
  "sungboon-editor",
  "vt",
  "medicube",
  "mediheal",
  "numbuzin",
  "clio",
  "wakemake",
  "ongredients",
  "juice-to-cleanse",
  "rataplan",
  "de-noah",
  "calvisana",
  "efilow",
  "wenlici",
  "bonabella",
  "the-palace-secret",
  "mihwanghoo",
  "real-derma",
  "melomeli",
];

export const orderedBrandDirectory = [...brandDirectory].sort((a, b) => {
  const aIndex = preferredBrandOrder.indexOf(a.slug);
  const bIndex = preferredBrandOrder.indexOf(b.slug);
  return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
});
